import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProfile, useRecurrences, useUpdateProfile } from '@/hooks/use-catalog';
import { PageHeader } from '@/components/layout/app-shell';
import {
  ApiError,
  Button,
  Card,
  Feedback,
  Field,
  LoadingState,
  SectionTitle,
  describeRecurrence,
  formatMoney,
  maskPhoneInput,
  useAuthStore,
  useTenant,
} from '@barber/ui';
import type { CustomerProfile } from '@barber/ui';
import { isValidCpf, normalizeCpf } from '@barber/shared';

/**
 * Perfil.
 *
 * ---
 *
 * O TELEFONE NÃO É EDITÁVEL AQUI
 *
 * Ele é a IDENTIDADE da conta — é por ele que o login acontece. Trocá-lo é
 * trocar de conta, e um campo de texto ao lado do nome faria isso parecer uma
 * edição qualquer. Quando existir troca de telefone, ela vai precisar de OTP no
 * número novo, que é outro fluxo.
 */
export function ProfilePage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const perfil = useProfile();
  const logout = useAuthStore((s) => s.logout);

  if (perfil.isPending) return <LoadingState />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Perfil" />

      {/*
        `key` no id do perfil: o formulário nasce JÁ com os dados carregados, em
        vez de nascer vazio e ser preenchido por um efeito. Sincronizar estado
        de formulário com dados de servidor dentro de `useEffect` produz um
        render intermediário com os campos em branco — e sobrescreve o que a
        pessoa estava digitando se a consulta revalidar no meio.
      */}
      {perfil.data && <PerfilForm key={perfil.data.id} perfil={perfil.data} />}
      {perfil.data && <PagamentoPix key={`pix-${perfil.data.id}`} perfil={perfil.data} />}

      <MeusHorariosFixos />

      <section>
        <SectionTitle>Mais</SectionTitle>

        <Card className="flex flex-col divide-y divide-border">
          <Link to="/historico" className="tap-target flex items-center py-3 text-sm text-content">
            Meu histórico
          </Link>

          {tenant.contact.whatsapp && (
            <a
              href={`https://wa.me/${tenant.contact.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noreferrer"
              className="tap-target flex items-center py-3 text-sm text-content"
            >
              Falar com a barbearia
            </a>
          )}

          {tenant.address.line && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                [tenant.address.line, tenant.address.district, tenant.address.city]
                  .filter(Boolean)
                  .join(', '),
              )}`}
              target="_blank"
              rel="noreferrer"
              className="tap-target flex flex-col py-3 text-sm text-content"
            >
              Como chegar
              <span className="text-xs text-content-muted">
                {tenant.address.line}
                {tenant.address.district ? `, ${tenant.address.district}` : ''}
              </span>
            </a>
          )}
        </Card>
      </section>

      <Button
        variant="ghost"
        onClick={() => {
          void logout().then(() => navigate('/entrar', { replace: true }));
        }}
      >
        Sair da conta
      </Button>

      <p className="pb-4 text-center text-xs text-content-muted">
        {tenant.name}
        {tenant.contact.phone ? ` · ${tenant.contact.phone}` : ''}
      </p>
    </div>
  );
}

/** O formulário em si, montado a partir dos dados já carregados. */
function PerfilForm({ perfil }: { perfil: CustomerProfile }) {
  const salvar = useUpdateProfile();

  const [nome, setNome] = useState(perfil.name);
  const [email, setEmail] = useState(perfil.email ?? '');
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const alterado = nome !== perfil.name || email !== (perfil.email ?? '');

  async function submeter(): Promise<void> {
    setErro(null);
    setAviso(null);

    try {
      await salvar.mutateAsync({ name: nome, email: email.trim() === '' ? null : email.trim() });
      setAviso('Dados atualizados.');
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Não conseguimos salvar agora.');
    }
  }

  return (
    <Card>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submeter();
        }}
      >
        <Field
          label="Nome"
          autoComplete="name"
          value={nome}
          onChange={(event) => setNome(event.target.value)}
        />

        <Field
          label="E-mail"
          type="email"
          autoComplete="email"
          placeholder="voce@email.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Field
          label="Telefone"
          value={maskPhoneInput(perfil.phoneE164.replace('+55', ''))}
          readOnly
          disabled
          hint="É por ele que você entra no app. Para trocar, fale com a barbearia."
        />

        {aviso && <Feedback tone="success">{aviso}</Feedback>}
        {erro && <Feedback tone="error">{erro}</Feedback>}

        <Button
          type="submit"
          fullWidth
          loading={salvar.isPending}
          disabled={!alterado || nome.trim().length < 2}
        >
          Salvar alterações
        </Button>
      </form>
    </Card>
  );
}

/**
 * O que o PIX precisa do cliente, e o que ele tem guardado nesta barbearia.
 *
 * O crédito vem de horário pago e não atendido. Não há botão para usar: ele é
 * descontado sozinho no próximo horário marcado aqui.
 */
function PagamentoPix({ perfil }: { perfil: CustomerProfile }) {
  const { tenant, locale } = useTenant();
  const salvar = useUpdateProfile();
  const [cpf, setCpf] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const credito = perfil.creditCents ?? 0;
  if (!tenant.booking.pix?.available && credito === 0 && !perfil.cpfHint) return null;

  async function salvarCpf(): Promise<void> {
    setErro(null);
    setAviso(null);

    if (!isValidCpf(cpf)) {
      setErro('CPF inválido. Confira os números.');
      return;
    }

    try {
      await salvar.mutateAsync({ cpf: normalizeCpf(cpf) });
      setCpf('');
      setAviso('CPF salvo.');
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Não conseguimos salvar agora.');
    }
  }

  return (
    <section>
      <SectionTitle>Pagamento</SectionTitle>

      <Card className="flex flex-col gap-3 text-sm">
        {credito > 0 && (
          <p className="text-content">
            Crédito nesta barbearia:{' '}
            <span className="font-semibold">{formatMoney(credito, locale)}</span>
            <span className="block text-xs text-content-muted">
              Usado sozinho no próximo horário que você marcar aqui.
            </span>
          </p>
        )}

        {perfil.cpfHint && (
          <p className="text-content">
            CPF para o PIX: <span className="font-mono">{perfil.cpfHint}</span>
          </p>
        )}

        <Field
          label={perfil.cpfHint ? 'Trocar CPF' : 'CPF para pagar por PIX'}
          inputMode="numeric"
          autoComplete="off"
          placeholder="000.000.000-00"
          value={cpf}
          onChange={(event) => setCpf(event.target.value)}
        />

        {aviso && <Feedback tone="success">{aviso}</Feedback>}
        {erro && <Feedback tone="error">{erro}</Feedback>}

        <Button
          variant="secondary"
          loading={salvar.isPending}
          disabled={cpf.trim().length < 11}
          onClick={() => void salvarCpf()}
        >
          Salvar CPF
        </Button>
      </Card>
    </section>
  );
}

/**
 * Os horários fixos do cliente.
 *
 * Só para enxergar: encerrar fica no detalhe de uma das semanas, ao lado de
 * "cancelar só esta semana" — é lá que a diferença entre as duas ações fica
 * clara.
 */
function MeusHorariosFixos() {
  const recorrencias = useRecurrences();
  const vivas = (recorrencias.data ?? []).filter((r) => r.status !== 'CANCELLED');

  if (vivas.length === 0) return null;

  return (
    <section>
      <SectionTitle>Meus horários fixos</SectionTitle>

      <Card className="flex flex-col divide-y divide-border">
        {vivas.map((r) => (
          <div key={r.id} className="py-3 text-sm">
            <p className="font-semibold text-content">
              {describeRecurrence(r)} · {r.barber.name}
            </p>
            <p className="text-content-muted">
              {r.service.name}
              {r.status === 'PENDING' && ' · aguardando a barbearia confirmar'}
            </p>
          </div>
        ))}
      </Card>
    </section>
  );
}
